import { CSSProperties } from 'react';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { Styles } from '@vivli/shared/theme';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { ICellRendererParams } from 'ag-grid-community';
import { DataRequestFormatService } from '@vivli/features/data-requests/infrastructure/service';
import { useActiveUserContext, useAssignedAppTypeContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { useTranslation } from 'react-i18next';
import { TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { CancelButtonView } from '../shared/cancel-button.component';
import { DTIDataRequest } from '@vivli/shared/infrastructure/constants';

const containerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    height: '100%'
};

const statusTextStyle: CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

// BUTTON_RED_X paints the X as a background image at the right edge and zeroes both paddings, so
// centered text in the 100px minimum width lands under the X. Left-align the text, as the card did,
// and reserve the X's width on the right.
const removeBtnStyle: CSSProperties = {
    ...Styles.Button.BUTTON_RED_X,
    flexShrink: 0,
    justifyContent: 'flex-start',
    paddingLeft: '12px',
    paddingRight: '36px',
    marginRight: 0
};

interface DataRequestStatusRendererComponentProps extends ICellRendererParams {
    data: IDataRequestSummary;
    isPresumedResearcher?: boolean;
}

// The Status cell: the displayed status text and, for the Request Admin of a Draft, the Cancel button.
export const DataRequestStatusRenderer = ({ data, isPresumedResearcher, api }: DataRequestStatusRendererComponentProps) => {
    const toast = useToastApiContext();
    const { t } = useTranslation();
    const duaTranslation = t(TranslationKeyEnum.duaValidatedStatusString);
    const { isAmr } = useAssignedAppTypeContext();
    const { user } = useActiveUserContext();
    const statusMessage = DataRequestFormatService.mapStatusToMessage(data.status, isAmr, duaTranslation);

    // DEV-958: Cancel is for the Request Admin only, meaning the request's owner, on Draft rows.
    // isPresumedResearcher keeps it off admin and provider views, as the card did.
    const canCancelDraft = !!isPresumedResearcher && data.status === DataRequestStatusEnum.Draft && !!user?.id && data.userId === user.id;

    const handleRemoveRow = () => {
        toast.success(`${data.requestTitle} successfully withdrawn.`);
        // DEV-1003: a paged (infinite) grid rejects transactions; reload its pages, which also refreshes the tab count
        if (api.getGridOption('rowModelType') === 'infinite') {
            api.refreshInfiniteCache();
        } else {
            api.applyTransaction({ remove: [data] });
        }
    };

    return (
        <div style={containerStyle}>
            <span style={statusTextStyle} title={statusMessage} data-test-id={DTIDataRequest.DataRequestStatus + data.requestTitle}>
                {statusMessage}
            </span>
            {canCancelDraft && <CancelButtonView dataRequest={data} style={removeBtnStyle} updateDataRequest={handleRemoveRow} />}
        </div>
    );
};
